import { extensionsConfig } from "client/config/extensionsConfig";
import { useGoogleDriveUpload } from "client/hooks/upload/useGoogleDriveUpload";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { AddIcon } from "components/common/Icons/AddIcon";
import { List } from "components/common/List/List";
import { Popup } from "components/common/Popup/Popup";
import { SmallContainer } from "components/common/SmallContainer/SmallContainer";
import { ToolTip } from "components/common/ToolTip/ToolTip";
import { DeviceUpload } from "components/common/Upload/DeviceUpload/DeviceUpload";
import { DropboxUpload } from "components/common/Upload/DropboxUpload/DropboxUpload";
import { GoogleDriveAccessErrorModal } from "components/common/Upload/GoogleDriveUpload/GoogleDriveAccessErrorModal/GoogleDriveAccessErrorModal";
import { GoogleDriveUpload } from "components/common/Upload/GoogleDriveUpload/GoogleDriveUpload";
import { UrlUpload } from "components/common/Upload/UrlUpload/UrlUpload";
import { useRouter } from "next/router";
import { useAppSelector } from "store/store";
import { useAddImages } from "./useAddImages";

export const AddImages = () => {
    const { pathname } = useRouter();
    const supportedExtensions = (extensionsConfig as any)[pathname?.split("/")[1] || ""] || extensionsConfig["image-compressor"];
    const { isOpen, toggle, onClose, handleFile } = useAddImages();
    const { state } = useAppSelector((state) => state.compress.downloadButtonState);
    const {
        handleClick: handleGoogleDriveUploadClick,
        accessError,
        setAccessError,
        isLoading: isGoogleDriveLoading,
    } = useGoogleDriveUpload(handleFile, { allowMultiple: true, supportedExtensions }, isOpen);

    const isActive = state === "active";

    return (
        <>
            <GoogleDriveAccessErrorModal
                isOpen={accessError}
                close={() => setAccessError(false)}
                handleRetry={handleGoogleDriveUploadClick}
            />
            <Popup
                isOpen={isOpen}
                close={onClose}
                content={
                    <div
                        className="absolute flex flex-col top-full my-2 left-0 bg-white dark:bg-darkSurface-200 rounded-xl shadow-lg border border-irGray-400 dark:border-darkSurface-100 overflow-hidden"
                        data-testid="add-images-content"
                    >
                        <List
                            items={{
                                from_device: (
                                    <DeviceUpload
                                        handleFile={handleFile}
                                        options={{ allowMultiple: true, supportedExtensions }}
                                        htmlFileInputId="compressor-editor-file-input"
                                    />
                                ),
                                dropbox: (
                                    <DropboxUpload
                                        handleFile={handleFile}
                                        options={{ allowMultiple: true, supportedExtensions }}
                                        isOpen={isOpen}
                                    />
                                ),
                                google_drive: (
                                    <GoogleDriveUpload
                                        options={{
                                            allowMultiple: true,
                                            onClick: handleGoogleDriveUploadClick,
                                            supportedExtensions,
                                        }}
                                        isLoading={isGoogleDriveLoading}
                                    />
                                ),
                                url_upload: <UrlUpload handleFile={handleFile} options={{ supportedExtensions }} />,
                            }}
                        />
                    </div>
                }
            >
                <ToolTip text={capitalize(translateFunc("upload"))}>
                    <SmallContainer
                        className={`cursor-pointer flex border border-irGray-400 dark:border-darkSurface-100 rounded-xl overflow-hidden h-full py-3  ${
                            isOpen
                                ? "bg-irGray-200 dark:bg-darkSurface-200"
                                : "hover:bg-irGray-200 dark:bg-darkSurface-300"
                        } dark:hover:bg-darkSurface-200`}
                        onClick={() => isActive && toggle()}
                        data-testid="add-images-icon"
                    >
                        <AddIcon className="fill-black dark:fill-white" />
                    </SmallContainer>
                </ToolTip>
            </Popup>
        </>
    );
};
